"use client";

import { FormEvent, useEffect, useState } from "react";
import {
  applyPhoneMask,
  createLead,
  isValidRussianPhone,
} from "@/lib/bitrix24";
import type { CatalogItem, ModalMode, SelectedFilters } from "@/lib/types";

interface LeadModalProps {
  isOpen: boolean;
  mode: ModalMode;
  item: CatalogItem | null;
  selected: SelectedFilters;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const modalContent: Record<
  ModalMode,
  { title: string; subtitle: string; submitText: string; defaultNotes: string }
> = {
  lead: {
    title: "Оформить заявку на агрегат",
    subtitle:
      "Заявка отправляется менеджеру, и он предоставит информацию в течение 15 минут.",
    submitText: "Узнать цену",
    defaultNotes: "",
  },
  photo: {
    title: "📸 Запросить реальные фото агрегата",
    subtitle:
      "Укажите номер телефона, и наш кладовщик вышлет живые фото и видео осмотра агрегата на MAX / Telegram в течение 10 минут.",
    submitText: "Получить живые фото",
    defaultNotes: "Запрос реальных фото и видео детали со склада",
  },
  callback: {
    title: "📞 Заказать звонок менеджера",
    subtitle:
      "Укажите имя и номер телефона, инженер по подбору ДВС и КПП перезвонит вам в течение 5 минут.",
    submitText: "Заказать обратный звонок",
    defaultNotes: "Запрос обратного звонка менеджера",
  },
};

export function LeadModal({
  isOpen,
  mode,
  item,
  selected,
  onClose,
  onSuccess,
  onError,
}: LeadModalProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const content = modalContent[mode];

  useEffect(() => {
    if (isOpen) {
      setNotes(content.defaultNotes);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, content.defaultNotes]);

  if (!isOpen) return null;

  const selText = [
    selected.brand,
    selected.model,
    selected.fuel,
    selected.displacement,
    selected.power,
  ]
    .filter(Boolean)
    .join(" ");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      onError("Пожалуйста, введите ваше Имя");
      return;
    }

    if (!isValidRussianPhone(phone)) {
      onError(
        "Укажите корректный российский номер телефона в формате +7 (9XX) XXX-XX-XX",
      );
      return;
    }

    setSubmitting(true);
    const result = await createLead(
      {
        name: name.trim(),
        phone: phone.trim(),
        item,
        additionalNotes: notes.trim(),
      },
      selected,
    );
    setSubmitting(false);
    onClose();

    if (result.success) {
      onSuccess(
        "Ваша заявка принята! Менеджер свяжется с вами в ближайшее время.",
      );
    } else {
      onError(result.error || "Ошибка отправки заявки");
    }

    setName("");
    setPhone("");
    setNotes("");
  };

  return (
    <div className="modal-window open" id="leadModal">
      <div className="modal-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="modal-dialog">
        <button type="button" className="modal-close-btn" onClick={onClose}>
          &times;
        </button>

        <h3 className="modal-title">{content.title}</h3>
        <p className="modal-subtitle">{content.subtitle}</p>

        <div id="leadItemSummary">
          {item ? (
            <div className="lead-summary-card">
              <div className="summary-title">
                {item.brand} {item.model} — {item.engineCode}
              </div>
              <div className="summary-specs">
                <span>Категория: {item.category}</span>
              </div>
            </div>
          ) : (
            <div className="lead-summary-card">
              <div className="summary-title">Индивидуальный подбор ДВС / КПП</div>
              <div className="summary-specs">
                Параметры: {selText || "Любая марка и деталь"}
              </div>
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <div className="form-group">
            <label className="form-label" htmlFor="leadName">
              Ваше Имя *
            </label>
            <input
              type="text"
              id="leadName"
              className="form-input"
              placeholder="например, Александр"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="leadPhone">
              Номер телефона *
            </label>
            <input
              type="tel"
              id="leadPhone"
              className="form-input"
              placeholder="+7 (___) ___-__-__"
              required
              value={phone}
              onChange={(e) => setPhone(applyPhoneMask(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && phone.length <= 4) {
                  setPhone("");
                }
              }}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="leadNotes">
              Город доставки / Комментарий
            </label>
            <input
              type="text"
              id="leadNotes"
              className="form-input"
              placeholder="например, Екатеринбург"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <button
            type="submit"
            className="btn btn-orange"
            style={{
              justifyContent: "center",
              padding: 14,
              marginTop: 8,
            }}
            disabled={submitting}
          >
            <span>{submitting ? "Отправка..." : content.submitText}</span>
          </button>
        </form>
      </div>
    </div>
  );
}
